import React from 'react'
import styled from 'styled-components/macro'
import Icon from 'components/atoms/icon'
import PropTypes from 'prop-types'
import { media } from 'utilities/styled'

const StyledAdvertisementCarCard = styled.div`
  background: ${({ theme, $iconOnly }) =>
    $iconOnly ? 'transparent' : theme.colors.fieldBackground};
  padding: ${({ theme }) => theme.sizeByFactor(1.5)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizeByFactor(3)};
  cursor: pointer;

  span {
    box-sizing: initial;
    padding-right: ${({ theme }) => theme.sizeByFactor(1.2)};

    path:first-child {
      fill: ${({ theme, $greyIcon }) => $greyIcon && theme.colors.errorIcon};
    }
  }

  .divider {
    width: 1px;
    height: 100%;
    background: ${({ theme }) => theme.colors.inactiveBackground};
  }
`

const StyledAdsSummaryWrapper = styled.div`
  position: relative;
  font-size: ${({ theme }) => theme.smallFontSize};
`

const StyledSummary = styled.div`
  width: ${({ theme, $listView }) =>
    $listView ? theme.sizeByFactor(2.5) : theme.sizings.lvl3};
  height: ${({ theme, $listView }) =>
    $listView ? theme.sizeByFactor(2.5) : theme.sizings.lvl3};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.defaultBackground};

  ${media.desktop`
    width: ${({ theme }) => theme.sizings.lvl3};
    height: ${({ theme }) => theme.sizings.lvl3};
  `}
`

const StyledSummaryInfoTotal = styled.div`
  background: ${({ theme }) => theme.colors.darkBackground};
  border-radius: 50%;
  position: absolute;
  top: -5px;
  right: 5px;
  width: ${({ theme }) => theme.sizings.lvl2};
  height: ${({ theme }) => theme.sizings.lvl2};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.defaultBackground};
`

const StyledSummaryInfoPublished = styled(StyledSummary)`
  background: ${({ theme }) => theme.colors.statePositive};
`

const StyledSummaryInfoPending = styled(StyledSummary)`
  background: ${({ theme }) => theme.colors.statusNeutral};
`

const StyledSummaryInfoError = styled(StyledSummary)`
  background: ${({ theme }) => theme.colors.errorText};
  width: ${({ theme, $listView }) =>
    $listView ? theme.sizeByFactor(2.5) : theme.sizings.lvl3};
  height: ${({ theme, $listView }) =>
    $listView ? theme.sizeByFactor(2.5) : theme.sizings.lvl3};
`

const StyledSummaryInfoTotalMobile = styled(StyledSummary)`
  background: ${({ theme }) => theme.colors.darkBackground};
  width: ${({ theme, $listView }) =>
    $listView ? theme.sizeByFactor(2.5) : theme.sizings.lvl3};
  height: ${({ theme, $listView }) =>
    $listView ? theme.sizeByFactor(2.5) : theme.sizings.lvl3};
`

const StyledSummaryInfoUnpublished = styled(StyledSummary)`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.inactiveBackground};
  color: ${({ theme }) => theme.colors.text};
`

function AdvertisementCarCard({
  className,
  iconOnly,
  onClick,
  advertisementData,
  listView,
}) {
  const { total, published, pending, error, unpublished } = advertisementData

  if (iconOnly) {
    if (error > 0) {
      return (
        <StyledAdvertisementCarCard
          $iconOnly={iconOnly}
          className={className}
          onClick={onClick}
        >
          <StyledSummary $listView={listView}>
            <StyledSummaryInfoError $listView={listView}>
              {error}
            </StyledSummaryInfoError>
          </StyledSummary>
        </StyledAdvertisementCarCard>
      )
    } else {
      return (
        <StyledAdvertisementCarCard
          $iconOnly={iconOnly}
          className={className}
          onClick={onClick}
        >
          <StyledSummary $listView={listView}>
            <StyledSummaryInfoTotalMobile
              $iconOnly={iconOnly}
              $listView={listView}
            >
              {total}
            </StyledSummaryInfoTotalMobile>
          </StyledSummary>
        </StyledAdvertisementCarCard>
      )
    }
  } else {
    return (
      <StyledAdvertisementCarCard className={className} onClick={onClick}>
        <StyledAdsSummaryWrapper>
          <Icon size="lg" type="published" />
          <StyledSummaryInfoTotal>{total}</StyledSummaryInfoTotal>
        </StyledAdsSummaryWrapper>
        <StyledSummary>
          <StyledSummaryInfoPublished>{published}</StyledSummaryInfoPublished>
        </StyledSummary>
        <StyledSummary>
          <StyledSummaryInfoPending>{pending}</StyledSummaryInfoPending>
        </StyledSummary>
        <StyledSummary>
          <StyledSummaryInfoError>{error}</StyledSummaryInfoError>
        </StyledSummary>
        <StyledSummary>
          <StyledSummaryInfoUnpublished>
            {unpublished}
          </StyledSummaryInfoUnpublished>
        </StyledSummary>
      </StyledAdvertisementCarCard>
    )
  }
}

AdvertisementCarCard.defaultProps = {
  className: '',
  iconOnly: false,
  onClick: undefined,
  listView: false,
}

AdvertisementCarCard.propTypes = {
  className: PropTypes.string,
  iconOnly: PropTypes.bool,
  onClick: PropTypes.func,
  advertisementData: PropTypes.object,
  listView: PropTypes.bool,
}

export default AdvertisementCarCard
